import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LeaderboardResponse,
  NamedBoss,
  getBattlesWonLeaderboard,
  getTilesUnlockedLeaderboard,
  getWeeklyChallengesLeaderboard,
  getBossClearLeaderboard,
  getNamedBosses
} from '../utils/leaderboards';
import './Leaderboards.css';

type BoardKey = 'battles-won' | 'tiles-unlocked' | 'weekly-challenges' | 'boss-clear';

const BOARDS: { key: BoardKey; label: string; icon: string; unit: string; lowerIsBetter?: boolean }[] = [
  { key: 'battles-won', label: 'Battles Won', icon: '⚔️', unit: 'wins' },
  { key: 'tiles-unlocked', label: 'Tiles Unlocked', icon: '🗺️', unit: 'tiles' },
  { key: 'weekly-challenges', label: 'Weekly Challenges', icon: '🗓️', unit: 'completed' },
  { key: 'boss-clear', label: 'Fastest Boss Clear', icon: '👑', unit: 'turns', lowerIsBetter: true }
];

const Leaderboards: React.FC = () => {
  const { user } = useAuth();
  const [activeBoard, setActiveBoard] = useState<BoardKey>('battles-won');
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [bosses, setBosses] = useState<NamedBoss[]>([]);
  const [selectedBossId, setSelectedBossId] = useState<number | null>(null);

  useEffect(() => {
    getNamedBosses().then((list) => {
      setBosses(list);
      if (list.length > 0) {
        setSelectedBossId((prev) => prev ?? list[0].monsterId);
      }
    });
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      let result: LeaderboardResponse;
      switch (activeBoard) {
        case 'battles-won':
          result = await getBattlesWonLeaderboard();
          break;
        case 'tiles-unlocked':
          result = await getTilesUnlockedLeaderboard();
          break;
        case 'weekly-challenges':
          result = await getWeeklyChallengesLeaderboard();
          break;
        case 'boss-clear':
          if (!selectedBossId) {
            result = { entries: [], yourRank: null, yourValue: null };
          } else {
            result = await getBossClearLeaderboard(selectedBossId);
          }
          break;
      }
      setData(result);
    } catch (error) {
      logger.error('Error loading leaderboard:', error);
      setData({ entries: [], yourRank: null, yourValue: null });
    } finally {
      setLoading(false);
    }
  }, [activeBoard, selectedBossId]);

  useEffect(() => {
    if (activeBoard !== 'boss-clear' || selectedBossId) {
      loadBoard();
    }
  }, [loadBoard, activeBoard, selectedBossId]);

  const board = BOARDS.find((b) => b.key === activeBoard)!;

  return (
    <div className="leaderboards-container">
      <div className="leaderboards-header">
        <h2>Leaderboards</h2>
      </div>

      <div className="leaderboards-tabs">
        {BOARDS.map((b) => (
          <button
            key={b.key}
            className={`lb-tab-button ${activeBoard === b.key ? 'active' : ''}`}
            onClick={() => setActiveBoard(b.key)}
          >
            <span className="lb-tab-icon">{b.icon}</span>
            {b.label}
          </button>
        ))}
      </div>

      {activeBoard === 'boss-clear' && (
        bosses.length > 0 ? (
          <div className="lb-boss-picker">
            <label htmlFor="lb-boss-select">Boss:</label>
            <select
              id="lb-boss-select"
              value={selectedBossId ?? ''}
              onChange={(e) => setSelectedBossId(Number(e.target.value))}
            >
              {bosses.map((b) => (
                <option key={b.monsterId} value={b.monsterId}>
                  {b.name}{b.townName ? ` (${b.townName})` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="lb-empty-note">No named bosses have been defeated yet.</p>
        )
      )}

      {loading ? (
        <div className="lb-loading">Loading rankings...</div>
      ) : (
        <>
          {data && data.yourRank !== null && (
            <div className="lb-your-rank-card">
              <span className="lb-your-rank-label">Your Rank</span>
              <span className="lb-your-rank-value">#{data.yourRank}</span>
              <span className="lb-your-rank-stat">
                {data.yourValue} {board.unit}
              </span>
            </div>
          )}

          {data && data.entries.length === 0 ? (
            <div className="lb-empty-note">No rankings yet for this board.</div>
          ) : (
            <div className="lb-table">
              <div className="lb-row lb-row-header">
                <span className="lb-col-rank">Rank</span>
                <span className="lb-col-name">Player</span>
                <span className="lb-col-value">{board.unit}</span>
              </div>
              {data && data.entries.map((entry) => (
                <div
                  key={entry.userId}
                  className={`lb-row ${entry.username === user?.username ? 'lb-row-you' : ''} ${entry.rank <= 3 ? `lb-row-top${entry.rank}` : ''}`}
                >
                  <span className="lb-col-rank">
                    {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                  </span>
                  <span className="lb-col-name">{entry.username}</span>
                  <span className="lb-col-value">{entry.value}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboards;
